import React, { useEffect, useState, useRef } from 'react';
import { z } from 'zod';
import { format, parseISO, addDays } from 'date-fns';
import { useForm } from 'react-hook-form';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  healthQuestionsSchemaWithConditional,
  HealthQuestionsSchema,
  OnboardingSchema,
} from '@features/onboarding/schema/onboarding';

import { Form } from '@components/ui/form';

import { useFinalSubmissionStore } from 'src/store/final-submission';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { useInsuranceStore } from 'src/store/insurance';
import { HealthIssueDetail } from 'src/services/interfaces/health';
import { Medication } from 'src/services/interfaces/application';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useHealthStore } from 'src/store/health';
// import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
// import { Application } from 'src/services/interfaces/application';
// import { onboardingSchema } from '@features/onboarding/schema/onboarding';

// import { MedicalConditionsIntroStep } from './components/medical-conditions-intro/MedicalConditionsIntroStep';
import { HealthConditionsSliderStep } from './components/health-conditions-slider/HealthConditionsSliderStep';
import { CoverageStartDateStep } from './components/coverage-start-date/CoverageStartDateStep';
import { PrivacyDisclosureStep } from './components/privacy-disclosure/PrivacyDisclosureStep';

import { getStepText } from './stepText';
import { HEALTH_QUESTIONS, REQUIRE_DETAILS } from './constants';
import { TakingMedicationsNowStep } from './components/medication-question/TakingMedicationsNowStep';
import { getHealthStepSchemaFromText } from './schema/phq';
import { InitialQuestionsStep } from './components/initial-questions/InitialQuestionsStep';
import { RiveSignatureAnimation } from './components/privacy-disclosure/RiveAnimation';
import { useApplicationStore } from 'src/store/application';
import { useAIStore } from 'src/store/ai';
import { applicationService } from 'src/services/application';
import { HIDE_DETAILS_FOR } from '@features/onboarding/components/6-health-question-form/constants';
import { FormStepSections } from '@features/onboarding/enums/formSections.enum';
import { motion, AnimatePresence, cubicBezier, useReducedMotion } from 'framer-motion';

const EASE = cubicBezier(0.22, 1, 0.36, 1);

const usePageTransitionVariants = (delay = 0) => {
  const shouldReduceMotion = useReducedMotion();

  return {
    initial: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20,
      filter: shouldReduceMotion ? 'none' : 'blur(4px)',
    },
    animate: {
      opacity: 1,
      y: 0,
      filter: 'none',
      transition: {
        duration: 0.6,
        ease: EASE,
        delay,
      },
    },
    exit: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : -10,
      filter: shouldReduceMotion ? 'none' : 'blur(2px)',
      transition: {
        duration: 0.4,
        ease: EASE,
      },
    },
  };
};


export const HealthQuestionForm = ({ onSubmit, onInvalid, defaultStep = 0 }: FormStepProps) => {
  const filteredHealthQuestions = defaultStep > 0 ? HEALTH_QUESTIONS.filter((_, index) => index === defaultStep) : HEALTH_QUESTIONS.filter((_) => !_.skip)

  // Ref to store timeout IDs for cleanup
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  /**
   * 🔀 Handles custom step skipping logic and synchronizes slider tab for onboarding steps.
   * Call this before default step navigation. Returns true if a custom switch was performed.
   * @param direction 'next' or 'prev' - direction of navigation
   */
  function switchStepWithSliderSync({
    direction,
    currentStep,
    form,
    filteredHealthQuestions,
    setCurrentStep,
  }: {
    direction: 'next' | 'prev';
    currentStep: number;
    form: any;
    filteredHealthQuestions: any[];
    setCurrentStep: (n: number) => void;
  }): boolean {
    // Get step indexes
    const idxSurgeriesDiabetesHeartMental = filteredHealthQuestions.findIndex(
      (q) => q.id === 'surgeriesDiabetesHeartMental'
    );
    const idxMedDetails = filteredHealthQuestions.findIndex(
      (q) => q.id === 'medDetails'
    );
    const idxOtherHealthConditions = filteredHealthQuestions.findIndex(
      (q) => q.id === 'otherHealthConditions'
    );
    const idxCoverageStart = filteredHealthQuestions.findIndex(
      (q) => q.id === 'coverageStartDate'
    );

    const idxPrivacyDisclosure = filteredHealthQuestions.findIndex(
      (q) => q.id === 'privacyDisclosure'
    );

    // Get form values
    const initialQ1 = form.getValues(
      'healthQuestions.initialQuestions.treatment'
    );
    const selectedIssues =
      form.getValues('healthQuestions.surgeriesDiabetesHeartMental.conditions') ||
      [];
    const selectedConditions =
      form.getValues('healthQuestions.medDetails.medications') || [];

    // NEXT direction logic
    if (direction === 'next') {
      if (
        currentStep === idxSurgeriesDiabetesHeartMental &&
        selectedIssues[0]?.value === 'none' &&
        initialQ1 === false
      ) {
        setCurrentStep(defaultStep > 0 ? idxCoverageStart : idxPrivacyDisclosure);
        return true;
      }
      if (
        selectedIssues[0]?.value !== 'none' &&
        initialQ1 === false &&
        currentStep === idxMedDetails
      ) {
        if (!selectedConditions.length) {
          return false;
        }
        setCurrentStep(idxCoverageStart);
        return true;
      }
      if (
        currentStep === idxSurgeriesDiabetesHeartMental &&
        initialQ1 === true &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(idxOtherHealthConditions);
        return true;
      }
    }
    // PREV direction logic
    if (direction === 'prev') {
      if (
        currentStep === idxPrivacyDisclosure &&
        initialQ1 === false &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(
          defaultStep > 0 ? idxCoverageStart : idxSurgeriesDiabetesHeartMental
        );
        return true;
      }
      if (
        currentStep === idxCoverageStart &&
        initialQ1 === false &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(idxSurgeriesDiabetesHeartMental);
        return true;
      }
      if (
        currentStep === idxCoverageStart &&
        initialQ1 === false &&
        selectedIssues[0]?.value !== 'none'
      ) {
        setCurrentStep(idxMedDetails);
        return true;
      }
      if (
        currentStep === idxOtherHealthConditions &&
        initialQ1 === true &&
        selectedIssues[0]?.value === 'none'
      ) {
        setCurrentStep(idxSurgeriesDiabetesHeartMental);
        return true;
      }
    }
    return false;
  }

  const {
    data,
    isLastSelectedPHQ,
    setData,
    setLastSelectedPHQ,
    setPhqSubmitted,
    registerForm,
    unregisterForm,
  } = useOnboardingStore();
  const { handleJumpTo, handleNextStep: handleNextStepGlobal } = useFormControls();
  const { issues, conditions, issueDetails, setFailValidation } =
    useHealthStore();
  const [showAnimationScreen, setShowAnimationScreen] = useState(false);
  const currentApplicationId = useApplicationStore((state) => state.currentApplicationId);

  // Move the hook call to the top level
  const pageTransitionVariants = usePageTransitionVariants();
  const idxPrivacyDisclosure = filteredHealthQuestions.findIndex(
    (q) => q.id === 'privacyDisclosure'
  );
  const {
    isUnderwritingLoading,
    underwritingResult,
    underwritingError,
    isSubmittingApplication,
    runUnderwriting,
    getEstimation,
  } = useFinalSubmissionStore();
  const plans = useInsuranceStore((state) => state.plans);

  const form = useForm<HealthQuestionsSchema>({
    resolver: zodResolver(healthQuestionsSchemaWithConditional),
    defaultValues: {
      healthQuestions: {
        initialQuestions: {
          treatment:
            data.healthQuestions?.initialQuestions?.treatment ?? undefined,
          invalid: data.healthQuestions?.initialQuestions?.invalid ?? undefined,
          pregnancy:
            data.healthQuestions?.initialQuestions?.pregnancy ?? undefined,
        },
        takingMedicationsNow: {
          value: data.healthQuestions?.takingMedicationsNow?.value ?? undefined,
        },
        surgeriesDiabetesHeartMental: {
          conditions:
            data.healthQuestions?.surgeriesDiabetesHeartMental?.conditions ??
            [],
          details:
            data.healthQuestions?.surgeriesDiabetesHeartMental?.details ?? {},
        },
        medDetails: {
          medications: data.healthQuestions?.medDetails?.medications ?? [],
          details: data.healthQuestions?.medDetails?.details ?? {},
        },
        otherHealthConditions: {
          conditions:
            data.healthQuestions?.otherHealthConditions?.conditions ?? [],
          details: data.healthQuestions?.otherHealthConditions?.details ?? {},
        },
        finalHealthConditions: {
          value:
            data.healthQuestions?.finalHealthConditions?.value ?? undefined,
        },
        coverageStartDate: {
          effectiveDate:
            data.healthQuestions?.coverageStartDate?.effectiveDate ?? undefined,
        },
        privacyDisclosure: {
          disclaimer:
            data.healthQuestions?.privacyDisclosure?.disclaimer ?? false,
          signature: data.healthQuestions?.privacyDisclosure?.signature ?? '',
          accepted: data.healthQuestions?.privacyDisclosure?.accepted ?? false,
        },
      },
      ...data,
    },
    mode: 'onChange',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const [stepErrorMessage, setStepErrorMessage] = useState<string | null>(null);
  useEffect(() => {
    if (filteredHealthQuestions.length === 0) {
      return;
    }
    setCurrentStep((prev) => {
      if (prev < 0) return 0;
      const maxIndex = filteredHealthQuestions.length - 1;
      return prev > maxIndex ? maxIndex : prev;
    });
  }, [filteredHealthQuestions, filteredHealthQuestions.length]);

  const currentStepQuestion = filteredHealthQuestions[currentStep];

  useEffect(() => {
    if (!currentStepQuestion?.id) return;
    useAIStore.getState().setCurrentPage(`phq-${currentStepQuestion.id}`);
  }, [currentStepQuestion?.id]);


  const questionId = filteredHealthQuestions[currentStep]?.id;
  const { title, description } = getStepText(questionId);

  useEffect(() => {
    if (isLastSelectedPHQ) {
      console.log('isLastSelectedPHQ', isLastSelectedPHQ);
      // setActiveCategoryIndex(planCategories.length - 1);
      setCurrentStep(idxPrivacyDisclosure);
      setLastSelectedPHQ(false);
    }
  }, [isLastSelectedPHQ, setCurrentStep, setLastSelectedPHQ, idxPrivacyDisclosure]);

  useEffect(() => {
    registerForm('healthQuestionForm', (data) => {
      form.reset(data);
    });

    return () => {
      unregisterForm('healthQuestionForm');
    };
  }, [form, registerForm, unregisterForm]);

  useEffect(() => {
    const subscription = form.watch((watchValues) => {
      const watchedValues = watchValues as Partial<HealthQuestionsSchema>;
      const createDefaultHealthQuestions = (): OnboardingSchema['healthQuestions'] => ({
        initialQuestions: {
          treatment: false,
          invalid: false,
          pregnancy: false,
        },
        takingMedicationsNow: {
          value: false,
        },
        surgeriesDiabetesHeartMental: {
          conditions: [],
          details: {},
        },
        medDetails: {
          medications: [],
          details: {},
        },
        otherHealthConditions: {
          conditions: [],
          details: {},
        },
        finalHealthConditions: {
          value: undefined,
        },
        coverageStartDate: {
          effectiveDate: undefined,
        },
        privacyDisclosure: {
          disclaimer: false,
          accepted: false,
          signature: '',
        },
      });

      const mergeHealthQuestions = (
        target: OnboardingSchema['healthQuestions'],
        source?: Partial<OnboardingSchema['healthQuestions']>
      ) => {
        if (!source) return target;
        (Object.keys(source) as Array<keyof OnboardingSchema['healthQuestions']>).forEach((key) => {
          const value = source[key];
          if (value !== undefined) {
            (target as Record<string, unknown>)[key as string] = value as unknown;
          }
        });
        return target;
      };

      const mergedHealthQuestions = mergeHealthQuestions(
        mergeHealthQuestions(
          createDefaultHealthQuestions(),
          data.healthQuestions as Partial<OnboardingSchema['healthQuestions']> | undefined
        ),
        watchedValues.healthQuestions as Partial<OnboardingSchema['healthQuestions']> | undefined
      );

      const mergedData: Partial<OnboardingSchema> = {
        ...data,
        healthQuestions: mergedHealthQuestions,
      };

      const transformedApplication = transformApplicationData(mergedData, plans);

      useApplicationStore.setState((state) => {
        const existingApplications = state.applications ?? [];
        const currentApplication = existingApplications[0];

        const mergedApplication = currentApplication
          ? {
              ...currentApplication,
              ...transformedApplication,
              id: currentApplication.id,
            }
          : transformedApplication;

        const isSame =
          currentApplication &&
          JSON.stringify(currentApplication) === JSON.stringify(mergedApplication);

        if (isSame) {
          return {};
        }

        const nextApplications =
          existingApplications.length > 0
            ? [mergedApplication, ...existingApplications.slice(1)]
            : [mergedApplication];

        return {
          applications: nextApplications,
        };
      });
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form, data, plans]);

  useEffect(() => {
    setStepErrorMessage(null);
    setFailValidation(false);
    
    // Clear any previous estimation errors when returning to health questions
    if (currentStep === 0) {
      useFinalSubmissionStore.setState({
        underwritingResult: null,
        underwritingError: null,
        isUnderwritingLoading: false,
      });
    }
  }, [currentStep]);

  // Cleanup timeout on unmount to prevent navigation after component is unmounted
  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        console.log('[HealthQuestionForm] Clearing navigation timeout on unmount');
        clearTimeout(navigationTimeoutRef.current);
        navigationTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (data.healthQuestions && Object.keys(data.healthQuestions).length > 0) {
      const formData = {
        healthQuestions: {
          initialQuestions: {
            treatment:
              data.healthQuestions.initialQuestions?.treatment ?? undefined,
            invalid:
              data.healthQuestions.initialQuestions?.invalid ?? undefined,
            pregnancy:
              data.healthQuestions.initialQuestions?.pregnancy ?? undefined,
          },
          takingMedicationsNow: {
            value:
              data.healthQuestions.takingMedicationsNow?.value ?? undefined,
          },
          surgeriesDiabetesHeartMental: {
            conditions:
              data.healthQuestions.surgeriesDiabetesHeartMental?.conditions ??
              [],
            details:
              data.healthQuestions.surgeriesDiabetesHeartMental?.details ?? {},
          },
          medDetails: {
            medications: data.healthQuestions.medDetails?.medications ?? [],
            details: data.healthQuestions.medDetails?.details ?? {},
          },
          otherHealthConditions: {
            conditions:
              data.healthQuestions.otherHealthConditions?.conditions ?? [],
            details: data.healthQuestions.otherHealthConditions?.details ?? {},
          },
          finalHealthConditions: {
            value:
              data.healthQuestions.finalHealthConditions?.value ?? undefined,
          },
          coverageStartDate: {
            effectiveDate:
              data.healthQuestions.coverageStartDate?.effectiveDate ??
              undefined,
          },
          privacyDisclosure: {
            disclaimer:
              data.healthQuestions.privacyDisclosure?.disclaimer ?? false,
            signature: data.healthQuestions.privacyDisclosure?.signature ?? '',
            accepted: data.healthQuestions.privacyDisclosure?.accepted ?? false,
          },
        },
        ...data,
      };

      const currentFormData = form.getValues();
      const currentSignature =
        currentFormData.healthQuestions?.privacyDisclosure?.signature;
      const newSignature =
        formData.healthQuestions?.privacyDisclosure?.signature;

      const signatureChanged = currentSignature !== newSignature;
      const otherDataChanged =
        JSON.stringify({
          ...currentFormData,
          healthQuestions: {
            ...currentFormData.healthQuestions,
            privacyDisclosure: {
              ...currentFormData.healthQuestions?.privacyDisclosure,
              signature: currentSignature,
            },
          },
        }) !==
        JSON.stringify({
          ...formData,
          healthQuestions: {
            ...formData.healthQuestions,
            privacyDisclosure: {
              ...formData.healthQuestions?.privacyDisclosure,
              signature: newSignature,
            },
          },
        });

      if (signatureChanged || otherDataChanged) {
        form.reset(formData);
      }
    }
  }, [data]);

  const isCurrentStepValid = (currentStep: number, formData: any): boolean => {
    const question = filteredHealthQuestions[currentStep];
    if (!question?.id) return false;

    const questionId = question.id;

    const stepSchema = getHealthStepSchemaFromText(questionId);
    if (!stepSchema) {
      setStepErrorMessage(null);
      return true;
    }

    const stepData = {
      healthQuestions: {
        [questionId]: formData.getValues(`healthQuestions.${questionId}`),
      },
    };

    try {
      stepSchema.parse(stepData);
      setStepErrorMessage(null);
      return true;
    } catch (err) {
      if (err instanceof z.ZodError) {
        setStepErrorMessage(
          err.errors[0]?.message || 'Please complete the current step.'
        );
      } else {
        setStepErrorMessage('Please complete the current step.');
      }
      return false;
    }
  };

  const handleNext = () => {
    if (defaultStep > 0) {
      console.log('its end')
      handleJumpTo('Finish Up');
      // setLastSelectedPHQ(true);
      return;
    }

    let nextIndex = currentStep + 1;
    // console.log(filteredHealthQuestions[nextIndex])
    const nextQuestion = filteredHealthQuestions[nextIndex]?.id;
    const nextQuestionSkip = filteredHealthQuestions[nextIndex]?.skip;
    // console.log(filteredHealthQuestions)
    if (nextQuestionSkip) {
      nextIndex = nextIndex + 1;
    }

    if (nextQuestion === 'currentMeds') {
      setCurrentStep(nextIndex + 1);
      return;
    }

    if (currentStep < filteredHealthQuestions.length - 1) {
      setCurrentStep(nextIndex);
    } else {
      // PHQ завершен - переходим на следующий шаг в общем flow (EstimateResult)
      console.log('[HealthQuestionForm] PHQ completed, navigating to next step in global flow');
      handleNextStepGlobal({});
      handleJumpTo(FormStepSections.EstimateResult);
    }
  };

  const handlePrevious = () => {
    const prevIndex = currentStep - 1;

    const currentId = filteredHealthQuestions[currentStep]?.id;
    const prevQuestion = filteredHealthQuestions[prevIndex]?.id;

    // if (
    //   currentId === 'medicalConditionsIntro' &&
    //   prevQuestion === 'currentMeds'
    // ) {
    //   setCurrentStep(prevIndex - 1);
    //   return;
    // }

    if (currentStep > 0) {
      setCurrentStep(prevIndex);
    }
  };

  const validationOtherHealthConditions1 = () => {
    const activeTabs =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) ?? [];
    const fields =
      form.getValues('healthQuestions.medDetails.medications') ?? [];

    const selectedAllElems = activeTabs.filter(e => e.value !== 'none').map(e => e.value);
    const selectedGroups = selectedAllElems.filter(e => HIDE_DETAILS_FOR.includes(e));

    if (selectedGroups.length === 0) {
      return true;
    }

    const onlyGroupValues = selectedAllElems.filter(e => !HIDE_DETAILS_FOR.includes(e));
    const fieldsByGroup: Record<string, any[]> = {};
    onlyGroupValues.forEach(field => {
      if (field) {
        const [groupName] = field.split('-');
        if (groupName && selectedGroups.includes(groupName)) {
          if (!fieldsByGroup[groupName]) {
            fieldsByGroup[groupName] = [];
          }
          fieldsByGroup[groupName].push(field);
        }
      }
    });

    for (const group of selectedGroups) {
      const groupFields = fieldsByGroup[group] || [];
      if (groupFields.length === 0) {
        return true;
      }
    }

    if (!REQUIRE_DETAILS) return false;

    type HealthIssueDetails = HealthIssueDetail & {
      parent?: string;
    };

    type GroupedHealthIssueDetails = {
      [parent: string]: HealthIssueDetails[];
    };

    const fieldsDetail: GroupedHealthIssueDetails =
      fields.reduce<GroupedHealthIssueDetails>((acc, e) => {
        const [parent, key] = e.value.split('-');
        const detail = issueDetails.find(
          (d) => d.key === key
        ) as HealthIssueDetails;

        if (detail && key !== 'none') {
          detail.parent = parent;
          if (!acc[parent]) {
            acc[parent] = [];
          }
          acc[parent].push(detail);
        } else if (key === 'none') {
          if (!acc[parent]) {
            acc[parent] = [];
          }
          acc[parent].push({
            key: 'none',
            label: 'NONE',
            issueId: NaN,
            type: NaN,
          });
        }

        return acc;
      }, {});

    for (let field of activeTabs) {
      if (!fieldsDetail[field.value]) {
        return true;
      }
      const groupFields = fields.filter((f) =>
        f.value.startsWith(`${field.value}-`)
      );
      for (let groupField of groupFields) {
        if (!groupField.value.endsWith('-none')) {
          if (!groupField?.details || groupField?.details.trim() === '') {
            return true;
          }
        }
      }
    }
    return false;
  };
  const validationTakingMedicationsNow = () => {
    const isTakingMedications = form.getValues('healthQuestions.takingMedicationsNow.value');

    // If user selected "NO", skip medication validation entirely
    if (isTakingMedications === false) {
      return false;
    }

    // @ts-ignore
    const fieldsMedications = (form.getValues('healthQuestions.takingMedicationsNow.medications') as Medication[]) ?? [];
    const hasInvalidFields = fieldsMedications?.some((field) => {
      const missingCore = !field.applicant || !field.dosage || !field.frequency;
      const missingDesc = !field.description;
      return REQUIRE_DETAILS ? missingCore || missingDesc : missingCore;
    });
    if (isTakingMedications && !fieldsMedications.length) {
      setFailValidation(true);
      return true;
    }
    return hasInvalidFields;
  };

  const localValidation = () => {
    const medDetails = filteredHealthQuestions.findIndex((q) => q.id === 'medDetails');
    const idxTakingMedicationsNow = filteredHealthQuestions.findIndex(
      (q) => q.id === 'takingMedicationsNow'
    );
    const idxOtherHealthConditions = filteredHealthQuestions.findIndex(
      (q) => q.id === 'otherHealthConditions'
    );

    const isInvalidTakingMedications =
      currentStep === idxTakingMedicationsNow
        ? validationTakingMedicationsNow()
        : false;
    const isInvalidOtherHealthConditions =
      currentStep === medDetails ? validationOtherHealthConditions1() : false;
    const isStepInvalid = !isCurrentStepValid(currentStep, form);
    const isInvalidDetails =
      currentStep === idxOtherHealthConditions ? validateDetails() : false;

    const isNotValid =
      isInvalidTakingMedications ||
      isInvalidOtherHealthConditions ||
      isStepInvalid ||
      isInvalidDetails ||
      switchStepWithSliderSync({
        direction: 'next',
        currentStep,
        form,
        filteredHealthQuestions,
        setCurrentStep,
      });

    // console.log('isNotValid', isNotValid);

    return !!isNotValid;
  };

  const validateDetails = () => {
    if (!REQUIRE_DETAILS) return false;
    const conditions =
      form.getValues('healthQuestions.otherHealthConditions.conditions') ?? [];
    const details =
      form.getValues('healthQuestions.otherHealthConditions.details') ?? {};

    for (const condition of conditions) {
      const value = condition.value;
      if (!HIDE_DETAILS_FOR.includes(value) && value !== 'none') {
        const detailValue = details[value];
        if (!detailValue || detailValue.trim() === '') {
          return true;
        }
      }
    }
    return false;
  };

  const onNextClick = async () => {
    if (localValidation()) {
      setFailValidation(true);
      return;
    }

    setFailValidation(false);

    const formData = form.getValues();

    setData(formData);

    if (defaultStep > 0) {
      handleNext();
      return;
    }

    if (filteredHealthQuestions[currentStep]?.id === 'privacyDisclosure') {
      const requestFormData = transformApplicationData(formData, plans);

      setShowAnimationScreen(true);
      setStepErrorMessage(null);

      // Mark PHQ as submitted before calling estimation
      setPhqSubmitted(true);

      const applicationState = await applicationService.applicationState();

      if (!applicationState?.phqAcceptedAt && !applicationState?.phqRejectedAt) {
        try {
          let estimationResult;

          if (currentApplicationId) {
            await applicationService.updateApplicationById(
              currentApplicationId,
              requestFormData
            );
            await getEstimation(currentApplicationId);
            // Get the result from store after estimation
            estimationResult = useFinalSubmissionStore.getState().underwritingResult;
          } else {
            await runUnderwriting(requestFormData);
            // Get the result from store after estimation
            estimationResult = useFinalSubmissionStore.getState().underwritingResult;
          }

          // Check if estimation returned a result
          if (!estimationResult) {
            // BUG FIX: Show error if estimation service fails
            setShowAnimationScreen(false);
            setStepErrorMessage('Estimation service is unavailable. Please try again later.');
            return;
          }

          // Handle different estimation statuses
          // Clear any existing timeout
          if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
          }

          // Navigate to EstimateResult step after animation
          navigationTimeoutRef.current = setTimeout(() => {
            setShowAnimationScreen(false);
            console.log('[HealthQuestionForm] Estimation complete, navigating to next step (EstimateResult)');
            handleJumpTo(FormStepSections.EstimateResult);
            navigationTimeoutRef.current = null;
          }, 3000);
        } catch (error) {
          // BUG FIX: Handle API errors properly - navigate to EstimateResult with rejection
          console.error('Error during estimation:', error);
          setShowAnimationScreen(false);
          
          // Set rejection result in store to show appropriate UI
          const rejectResult = {
            status: 'rejected' as const,
            details: {
              reason: 'API Error',
              error: 'Estimation service is unavailable. Please try again later.'
            }
          };
          
          // Update the store with rejection result
          useFinalSubmissionStore.setState({
            underwritingResult: rejectResult,
            underwritingError: 'Estimation service is unavailable. Please try again later.',
            isUnderwritingLoading: false,
          });
          
          // Navigate to EstimateResult step to show rejection screen
          console.log('[HealthQuestionForm] Estimation failed, navigating to EstimateResult with rejection status');
          handleJumpTo(FormStepSections.EstimateResult);
          return;
        }
      } else {
        // User already has accepted PHQ - go to next step (EstimateResult)
        // Clear any existing timeout
        if (navigationTimeoutRef.current) {
          clearTimeout(navigationTimeoutRef.current);
        }

        navigationTimeoutRef.current = setTimeout(() => {
          setShowAnimationScreen(false);
          console.log('[HealthQuestionForm] PHQ already accepted, navigating to next step');
          handleJumpTo(FormStepSections.EstimateResult);
          navigationTimeoutRef.current = null;
        }, 1000);
      }
    } else {
      handleNext();
    }
  };
  const renderAnimationScreen = () => {
    return (
      <div className="w-full h-full flex items-center justify-center bg-transparent">
        <RiveSignatureAnimation
          riveFileUrl="/anims/loader2.riv"
          width={300}
          height={150}
        />
      </div>
    );
  };

  const onPrevClick = async () => {
    setFailValidation(false);
    if (
      switchStepWithSliderSync({
        direction: 'prev',
        currentStep,
        form,
        filteredHealthQuestions,
        setCurrentStep,
      })
    ) {
      return;
    }

    handlePrevious();
  };

  const renderStep = () => {
    const currentQuestion = filteredHealthQuestions[currentStep];
    if (!currentQuestion) {
      return null;
    }

    const question = { ...currentQuestion };
    const errorMessage = stepErrorMessage;

    if (question.id === 'coverageStartDate') {
      const effectiveDate = form.getValues(
        'healthQuestions.coverageStartDate.effectiveDate'
      );
      if (effectiveDate) {
        const options = question.options as { label: string; value: string }[];
        const dateExists = options.some((opt) => opt.value === effectiveDate);

        if (!dateExists) {
          const date = parseISO(effectiveDate);
          const adjustedDate = addDays(date, 1);

          question.options = [
            ...options,
            {
              value: effectiveDate,
              label: format(adjustedDate, 'MMMM d, yyyy'),
            },
          ];
        }
      }
    }

    const stepIds = [
      'surgeriesDiabetesHeartMental',
      'medDetails',
      'otherHealthConditions',
      'finalHealthConditions',
    ];

    let questionsToPass = filteredHealthQuestions.filter((q) =>
      stepIds.includes(q.id)
    );

    // console.log(questionsToPass)

    if (issues.length > 0) {
      questionsToPass = [...questionsToPass];
      const surgeriesIdx = questionsToPass.findIndex(
        (q) => q.id === 'surgeriesDiabetesHeartMental'
      );
      if (surgeriesIdx !== -1) {
        questionsToPass[surgeriesIdx] = {
          ...questionsToPass[surgeriesIdx],
          options: [
            ...issues.map((v) => ({ label: v.label, value: v.key })),
            {
              label:
                "<strong>NONE APPLY</strong> – I've  read and understood the conditions above.",
              value: 'none',
              isNoneApply: true,
            },
          ],
        };
      }
    }
    if (conditions.length > 0) {
      const condIdx = questionsToPass.findIndex(
        (q) => q.id === 'otherHealthConditions'
      );
      if (condIdx !== -1) {
        questionsToPass[condIdx] = {
          ...questionsToPass[condIdx],
          options: [
            ...conditions.map((c) => ({ label: c.label, value: c.key })),
            {
              label:
                "<strong>NONE APPLY</strong> – I've  read and understood the conditions above.",
              value: 'none',
              isNoneApply: true,
            },
          ],
        };
      }
    }
    const detailsIdx = questionsToPass.findIndex((q) => q.id === 'medDetails');
    const selectedIssues =
      form.getValues(
        'healthQuestions.surgeriesDiabetesHeartMental.conditions'
      ) || [];
    if (detailsIdx !== -1 && issues.length > 0 && issueDetails.length > 0) {
      const grouped = issues
        .filter((issue) =>
          selectedIssues.map((v) => v.value).includes(issue.key)
        )
        .map((issue) => [
          ...issueDetails
            .filter((d) => d.issueId === issue.id)
            .map((d) => ({ label: d.label, value: d.key })),
          {
            label:
              "<strong>NONE APPLY</strong> – I've read and understood the conditions above.",
            value: 'none',
            isNoneApply: true,
          },
        ]);
      questionsToPass[detailsIdx] = {
        ...questionsToPass[detailsIdx],
        options: grouped,
      };
    }
    // console.log(questionsToPass)
    const activeTab = questionsToPass.findIndex((e) => e.id === currentQuestion.id);

    switch (question.id) {
      case 'initialQuestions':
        return <InitialQuestionsStep form={form} question={question} />;
      case 'takingMedicationsNow':
        return <TakingMedicationsNowStep form={form} question={question} />;
      // case 'medicalConditionsIntro':
      //   return <MedicalConditionsIntroStep form={form} question={question} />;
      case 'surgeriesDiabetesHeartMental':
      case 'medDetails':
      case 'otherHealthConditions':
      case 'finalHealthConditions':
        return (
          <HealthConditionsSliderStep
            questions={questionsToPass}
            activeTab={activeTab}
            form={form}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      case 'coverageStartDate':
        return (
          <CoverageStartDateStep
            form={form}
            question={question}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );
      case 'privacyDisclosure':
        return (
          <PrivacyDisclosureStep
            form={form}
            question={question}
            errorMessage={errorMessage}
            setStepErrorMessage={setStepErrorMessage}
          />
        );

      default:
        return null;
    }
  };

  // console.log('filteredHealthQuestions[currentStep]', filteredHealthQuestions[currentStep], currentStep)

  return (
    <Form {...form}>
      <OnboardingFormStepLayout
        handleFormSubmit={onNextClick}
        onPrev={currentStep > 0 ? onPrevClick : undefined}
        title={showAnimationScreen ? "Hold tight. We are consulting with the insurance elves..." : title}
        description={!showAnimationScreen ? description : ""}
        isFormValid={true}
        isLoading={
          showAnimationScreen ||
          isUnderwritingLoading ||
          isSubmittingApplication
        }
        isAnimationScreen={showAnimationScreen}
        backgroundClassName={showAnimationScreen ? 'bg-white' : undefined}
        headerAnimationKey={`health-questions-${currentStep}-${currentStepQuestion?.id ?? 'unknown'}`}
        contentAnimationKey={`health-questions-content-${currentStep}-${currentStepQuestion?.id ?? 'unknown'}`}
      >
        <div className="flex w-full rounded-2xl min-h-full max-2xl:mt-2 max-2xl:mb-6">
          <div className="flex-1 relative min-h-full">
            {showAnimationScreen ? (
              renderAnimationScreen()
            ) : (
              <AnimatePresence mode="wait">
                <motion.div
                  key={
                    defaultStep
                      ? 'coverageStartDate'
                      : currentStepQuestion?.id ?? `step-${currentStep}`
                  }
                  variants={pageTransitionVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  className="h-full"
                >
                  {defaultStep > 0 ? (
                    <CoverageStartDateStep
                      form={form}
                      question={filteredHealthQuestions[0]}
                      errorMessage={stepErrorMessage}
                      setStepErrorMessage={setStepErrorMessage}
                    />
                  ) : (renderStep())}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </div>
      </OnboardingFormStepLayout>
    </Form>
  );
};
